/**
 * @author Mevan Nirosh
 * @version 0.3
 * @since 01/01/2024
 *
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select from "@mui/material/Select"
import { Button, DialogActions, DialogContent, Fab } from "@mui/material"
import Grid2 from "@mui/material/Grid2"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { FetchErrorHandeling } from "../../../../../../base/errorHandeling/FetchErrorHandeling"
import api from "../../../../../../base/services/ResApi"
import noConnectionDtl from "../../../../../../../resources/noConncDtl200.gif"
import logsProcessing from "../../../../../../../resources/logsProcessing.gif"
import processComplete from "../../../../../../../resources/processComplete.gif"
import AodIcon from "@mui/icons-material/Aod"
import dayjs from "dayjs"
import Alerts from "../../../../../../base/errorHandeling/Alerts"

// ---------- helpers to read Axios blob error JSON (because responseType: "blob") ----------
const isBlob = (v) => typeof Blob !== "undefined" && v instanceof Blob

const safeJsonParse = (text) => {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const extractAxiosErrorData = async (error) => {
  const data = error?.response?.data

  if (data && typeof data === "object" && !isBlob(data)) return data

  if (isBlob(data)) {
    const text = await data.text()
    // try json parse even if content-type is missing/wrong
    return safeJsonParse(text) || { description: text }
  }

  if (typeof data === "string") return safeJsonParse(data) || { description: data }

  return null
}
// --------------------------------------------------------------------------------------

const NewLogs = ({ serialNo, approvedData, onClose }) => {
  const [isReqSent, setIsReqSent] = useState(false)
  const [logReqId, setLogReqId] = useState(null)

  const [logStatusIcon, setLogStatusIcon] = useState(noConnectionDtl)
  const [logDownloadStatus, setLogDownloadStatus] = useState("Not Available Yet")

  const [appLabel, setAppLabel] = useState("POS_PAYT")
  const [fromDate, setFromDate] = useState(dayjs())
  const [toDate, setToDate] = useState(dayjs())

  const [sendReq, setSendReq] = useState(false)

  // success messages
  const [isSuccess, setIsSuccess] = useState(false)
  const [successMessages, setSuccessMessages] = useState([])

  // error handling
  const [iserror, setIserror] = useState(false)
  const [iserrorShow, setIserrorShow] = useState(false)
  const [isUnauhtError, setIsUnauhtError] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])

  // ---------- polling guards ----------
  const intervalRef = useRef(null)
  const inFlightRef = useRef(false)
  const downloadedRef = useRef(false)
  const activeLogReqIdRef = useRef(null)

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    inFlightRef.current = false
    activeLogReqIdRef.current = null
  }, [])

  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  // clear all alerts after a timeout
  useEffect(() => {
    if (iserror || isSuccess) {
      const timerId = setTimeout(() => {
        setIserror(false)
        setErrorMessages([])
        setSuccessMessages([])
        setIsSuccess(false)
      }, 5000)

      return () => clearTimeout(timerId)
    }
  }, [iserror, isSuccess])

  const fromDateStr = useMemo(() => dayjs(fromDate).format("YYYY/MM/DD"), [fromDate])
  const toDateStr = useMemo(() => dayjs(toDate).format("YYYY/MM/DD"), [toDate])

  const logReqConfig = useMemo(
    () => ({
      headers: {
        "App-Label": appLabel,
        FD: fromDateStr,
        TD: toDateStr,
      },
    }),
    [appLabel, fromDateStr, toDateStr]
  )

  const logDownloadConfig = useMemo(
    () => ({
      headers: {
        "Log-Request-Id": logReqId,
        Action: "DOWNLOAD_NEW_LOG",
      },
      responseType: "blob",
    }),
    [logReqId]
  )

  const isDateRangeInvalid = useMemo(() => {
    if (!fromDate || !toDate) return true
    return dayjs(fromDate).isAfter(dayjs(toDate), "day")
  }, [fromDate, toDate])

  const isBusy = useMemo(() => sendReq || isReqSent, [sendReq, isReqSent])

  const resetUiForNewRequest = () => {
    setIsReqSent(false)
    setLogReqId(null)
    setLogStatusIcon(logsProcessing)
    setLogDownloadStatus("Requesting log download...")
    setIserror(false)
    setErrorMessages([])
    setIsUnauhtError(false)
    downloadedRef.current = false
  }

  const downloadZipFile = (binaryData, fileName) => {
    const blob = new Blob([binaryData], { type: "application/octet-stream" })
    const url = URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = url
    link.download = fileName

    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // -----------------------------
  // Request log generation (202)
  // -----------------------------
  useEffect(() => {
    if (!sendReq) return

    const run = async () => {
      try {
        resetUiForNewRequest()

        const res = await api.get(`/terminal/logs/${serialNo}`, logReqConfig)

        if (res.status === 202) {
          const reqId = res.headers?.["log-request-id"]
          if (!reqId) {
            setLogStatusIcon(noConnectionDtl)
            setLogDownloadStatus("Log request id missing. Please try again.")
            return
          }

          setLogReqId(reqId)
          setIsReqSent(true)
          setLogStatusIcon(logsProcessing)
          setLogDownloadStatus("Processing log download")
        }
      } catch (error) {
        setLogStatusIcon(noConnectionDtl)
        setLogDownloadStatus("Log request failed, Please try again")
        await FetchErrorHandeling(
          error,
          setIserror,
          setErrorMessages,
          setIserrorShow,
          setIsUnauhtError
        )
      } finally {
        setSendReq(false)
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendReq, serialNo, logReqConfig])

  // -----------------------------
  // Polling download (200 -> blob)
  // Keep polling for type: "log_not_uploaded_yet"
  // -----------------------------
  const startPolling = useCallback(
    (reqId) => {
      stopPolling()
      downloadedRef.current = false
      activeLogReqIdRef.current = reqId

      const fileName = `${serialNo}-${appLabel}-${fromDateStr}-${toDateStr}.zip`

      const pollOnce = async () => {
        if (activeLogReqIdRef.current !== reqId) return
        if (inFlightRef.current || downloadedRef.current) return

        inFlightRef.current = true

        try {
          const res = await api.get("/terminal/logs", logDownloadConfig)

          const blob = res?.data
          const isReady = res?.status === 200 && blob instanceof Blob && blob.size > 0

          if (isReady) {
            downloadedRef.current = true
            stopPolling()

            setIsReqSent(false)
            setLogStatusIcon(processComplete)
            setLogDownloadStatus("Processing complete")

            downloadZipFile(blob, fileName)

            setIsSuccess(true)
            setSuccessMessages(["Log download completed."])
          }
        } catch (error) {
          // Parse backend error payload (often comes as JSON blob because responseType is "blob")
          let errData = null
          try {
            errData = await extractAxiosErrorData(error)
          } catch {
            errData = null
          }

          const status = error?.response?.status
          const type = errData?.type

          // ✅ KEEP POLLING for this specific backend error
          if (status === 404 && type === "log_not_uploaded_yet") {
            // update UI to show it's still processing (no scary error popup)
            setLogStatusIcon(logsProcessing)
            setLogDownloadStatus(errData?.description || "Log is not uploaded yet, retrying...")

            // Optional: you can choose NOT to show error alert for this case
            setIserror(false)
            setErrorMessages([])

            // keep polling: do NOT stopPolling() and do NOT setIsReqSent(false)
            return
          }

          // ❌ Other errors -> stop polling and show error
          setIsReqSent(false)
          stopPolling()
          setLogStatusIcon(noConnectionDtl)
          setLogDownloadStatus("Log request failed, Please try again")

          await FetchErrorHandeling(
            error,
            setIserror,
            setErrorMessages,
            setIserrorShow,
            setIsUnauhtError
          )
        } finally {
          inFlightRef.current = false
        }
      }

      pollOnce()
      intervalRef.current = setInterval(pollOnce, 10000)
    },
    [
      stopPolling,
      serialNo,
      appLabel,
      fromDateStr,
      toDateStr,
      logDownloadConfig,
      FetchErrorHandeling,
    ]
  )

  useEffect(() => {
    if (isReqSent && logReqId) startPolling(logReqId)
  }, [isReqSent, logReqId, startPolling])

  const handleAppLabelChange = (e) => setAppLabel(e.target.value)

  const handleSendReq = () => {
    if (isDateRangeInvalid) {
      setIserror(true)
      setIserrorShow(true)
      setErrorMessages(["Invalid date range: From date cannot be after To date."])
      return
    }
    setSendReq(true)
  }

  const handleCancel = () => {
    stopPolling()
    onClose?.()
  }

  return (
    <div>
      <DialogContent
        dividers
        sx={{
          marginTop: -8,
          paddingTop: 10,
        }}
      >
        <div>
          <Alerts
            iserror={iserror}
            errorMessages={errorMessages}
            isSuccess={isSuccess}
            successMessages={successMessages}
            iserrorShow={iserrorShow}
          />
        </div>

        <Grid2 container spacing={3}>
          <Grid2 xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="app-label">App Label</InputLabel>
              <Select
                labelId="app-label"
                value={appLabel}
                label="App Label"
                onChange={handleAppLabelChange}
                disabled={isBusy}
              >
                <MenuItem value="POS_PAYT">POS Payment</MenuItem>
                <MenuItem value="ATOMS_CLT">ATOMS Client</MenuItem>
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dayjs(fromDate)}
                onChange={(val) => setFromDate(val)}
                label="From"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: isDateRangeInvalid,
                    helperText: isDateRangeInvalid ? "Invalid range" : "",
                  },
                }}
                disabled={isBusy}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2 xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dayjs(toDate)}
                onChange={(val) => setToDate(val)}
                label="To"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: isDateRangeInvalid,
                    helperText: isDateRangeInvalid ? "Invalid range" : "",
                  },
                }}
                disabled={isBusy}
              />
            </LocalizationProvider>
          </Grid2>
        </Grid2>

        <br />

        <Fab
          onClick={handleSendReq}
          variant="extended"
          color="primary"
          disabled={isBusy || isDateRangeInvalid}
        >
          <AodIcon sx={{ mr: 1 }} />
          {isReqSent ? "Requesting..." : "Request Log"}
        </Fab>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <img
            src={logStatusIcon}
            alt="Log Download status"
            className="logDownloadStatus"
          />
        </div>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <p>{logDownloadStatus}</p>
        </div>
      </DialogContent>

      <DialogActions sx={{ p: "1.25rem" }}>
        <Button onClick={handleCancel} variant="outlined" color="error">
          Cancel
        </Button>
      </DialogActions>
    </div>
  )
}

export default NewLogs